import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { take } from 'rxjs/operators';

import { AccountService } from '@app/services/account.service';
import { DeveloperDialogComponent } from '@app/components/developer-dialog/developer-dialog.component';
import { DisplaySettingsComponent } from '@app/components/display-settings/display-settings.component';
import { GuestSessionService } from '@app/services/guest-session.service';
import { LanguageSettingDialogComponent } from '@app/components/language-setting-dialog/language-setting-dialog.component';
import { Message } from '@app/shared/models/app.model';
import { Microphone } from '@app/shared/models/microphone.model';
import { MicrophoneSettingsComponent } from '@app/components/microphone-settings/microphone-settings.component';
import { SettingsService } from '@app/services/settings.service';
import { ShareDialogComponent } from '@app/components/share-dialog/share.component';
import { SpeechService } from '@app/services/speech.service';
import { TranslocoService } from '@jsverse/transloco';
import { TtsAudioService } from '@app/services/tts-audio.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  @ViewChild('header', { static: false }) el: ElementRef;
  @Input() noButtons: boolean;
  @Input() enableLogout: boolean;

  showDownloadButton = true;
  showLanguageButton = true;
  showShareButton = true;
  showMicrophoneSettingsButton = true;
  isDarkMode$ = this.settingsService.darkMode$;

  protected readonly ttsAudio = inject(TtsAudioService);
  private readonly qrUserService = inject(GuestSessionService);
  /** TTS read-along is a QR-guest-only feature behind a hardcoded device
   * rollout list, surfaced as an icon button on the speech page header.
   * Reactive because the device UUID can arrive after the header renders
   * (fetched from the license API during websocket init). */
  protected readonly showReadAlongButton = this.qrUserService.ttsReadAlongEnabled;

  inAppBrowser = !!(
    window['cordova_iab'] ?? window['webkit']?.messageHandlers['cordova_iab']
  );

  private readonly showAboutThreshold = 10;
  private showAboutClickCount = 0;

  constructor(
    private router: Router,
    private auth: AccountService,
    private speechService: SpeechService,
    private dialog: MatDialog,
    private transloco: TranslocoService,
    private settingsService: SettingsService
  ) {}

  ngOnInit() {
    // NOTE: Hide share, languages and save for any guest (QR or shared-session)
    if (this.qrUserService.isGuestUser()) {
      this.showDownloadButton =
        this.showLanguageButton =
        this.showShareButton =
        this.showMicrophoneSettingsButton =
          false;
    }
  }

  toggleReadAlong(): void {
    this.ttsAudio.setEnabled(!this.ttsAudio.enabled());
  }

  async logout() {
    if (this.inAppBrowser) {
      const target =
        window['cordova_iab'] ??
        window['webkit']?.messageHandlers['cordova_iab'];
      target?.postMessage(JSON.stringify({ command: 'clse' }));
    }

    await this.auth.logout();
    await this.router.navigate(['/login']);
  }

  openLanguageSettings() {
    this.dialog.open(LanguageSettingDialogComponent, {
      maxWidth: '400px',
      width: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  openMenuDisplaySettings() {
    this.dialog.open(DisplaySettingsComponent, {
      maxWidth: '600px',
      width: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  openMenuMicrophoneSettings() {
    this.dialog.open(MicrophoneSettingsComponent, {
      maxWidth: '1000px',
      width: '95vw',
      maxHeight: '90vh',
      autoFocus: false
    });
  }

  openDialogShare() {
    this.dialog.open(ShareDialogComponent, {
      maxWidth: '450px',
      width: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  public async downloadTranscript() {
    if (this.inAppBrowser) {
      alert(this.transloco.translate('header.cant_save_in_app'));
      return;
    }

    const mics = await this.speechService.activeMics$.pipe(take(1)).toPromise();
    if (!mics.length) {
      alert(this.transloco.translate('header.no_transcript'));
      return;
    }

    this.speechService.messages$
      .pipe(take(1))
      .subscribe((messages: Message[]) => {
        const text = messages
          .map((message: Message) => {
            const mic = mics.find(
              (_mic: Microphone) => message.micId === _mic.id
            );
            let micName = 'Microphone ' + message.micColor.name;
            if (mic) {
              micName = isNaN(+mic.name)
                ? mic.name
                : 'Microphone ' + message.micColor.name + ': ';
            }

            const result = `${micName} - ${this.formatTime(
              message.timestamp
            )}:\n${message.message}\n\n`;

            return result;
          })
          .join('\n');

        this.downloadTextFile(
          `Transcript-${this.formatDate(new Date(messages[0].timestamp))}`,
          text
        );
      });
  }

  clickLogo() {
    this.showAboutClickCount++;
    if (this.showAboutClickCount >= this.showAboutThreshold) {
      this.dialog.open(DeveloperDialogComponent);
      this.showAboutClickCount = 0;
    }
  }

  private formatTime(timestamp: any) {
    if (timestamp) {
      const date = new Date(timestamp);
      const hours = date.getHours();
      const minutes = date.getMinutes();

      let stringMinutes = String(minutes);

      if (minutes < 10) {
        stringMinutes = `0${minutes}`;
      }

      return `${hours}:${stringMinutes}`;
    }
  }

  private formatDate(timestamp: Date) {
    const date = new Date(timestamp);
    let month = '' + (date.getMonth() + 1);
    let day = '' + date.getDate();
    const year = date.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }

    return [year, month, day].join('-');
  }

  private downloadTextFile(fileName: string, text: string) {
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(text)
    );
    element.setAttribute('download', fileName);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }
}
